import { faAngleDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'

export type DropDownItem = {
    id: string
    value: React.ReactNode
}

type Props = {
    id: string
    items: DropDownItem[]
    name: string
    selectedItemId: string
    onItemSelected: (id: string) => void
}

function DropDown({
    id,
    items,
    name,
    selectedItemId,
    onItemSelected,
}: Props): React.JSX.Element {
    const [isOpen, setIsOpen] = useState(false)
    // Use a ref for the button container instead of the entire component.
    const buttonContainerRef = useRef<HTMLDivElement>(null)
    const [classes, setClasses] = useState('')

    // Toggle dropdown open/close
    const toggleDropdown = () => setIsOpen((prev) => !prev)

    // Close dropdown when clicking outside of the button container
    useEffect(() => {
        setClasses(
            clsx(
                'border inline-flex items-center select-none border-gray-300 rounded-full px-2 bg-white hover:border-ctxt-blue hover:bg-gray-100 cursor-pointer',
            ),
        )

        const handleClickOutside = (event: MouseEvent) => {
            if (
                buttonContainerRef.current &&
                !buttonContainerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    function handleItemSelected(item: DropDownItem) {
        onItemSelected(item.id)
        // close the dropdown
        setIsOpen(false)
    }

    return (
        <div id={id} className="flex gap-2 items-center">
            {/* Label */}
            {name && <div className="field-label">{name}</div>}

            {/* Wrap the button and dropdown content in a relative container */}
            <div className="relative" ref={buttonContainerRef}>
                {/* Button */}
                <div className={classes} onClick={toggleDropdown}>
                    <span>
                        {
                            items.find((item) => item.id === selectedItemId)
                                ?.value
                        }
                    </span>
                    <FontAwesomeIcon
                        icon={faAngleDown}
                        className="ml-2 transition-transform duration-200"
                        style={{
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                        }}
                    />
                </div>

                {/* Dropdown Content */}
                {isOpen && (
                    <div className="absolute left-0 top-full mt-1 w-auto min-w-[150px] bg-white border border-gray-300 shadow-lg rounded-md z-50 max-h-60 overflow-y-auto">
                        <div>
                            {items.map((item) => (
                                <div
                                    key={item.id}
                                    className="flex gap-2 cursor-pointer hover:bg-ctxt-blue-light hover:bg-opacity-10"
                                    onClick={() => handleItemSelected(item)}
                                >
                                    <label className="flex gap-2 py-1 px-2 cursor-pointer">
                                        <div className="text-nowrap">
                                            {item.value}
                                        </div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default DropDown
